/* eslint-disable max-len */
import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { MtmaAction } from 'src/app/model/mtma-action';
import { Command } from 'src/app/model/command';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { TAGS_DEFAULT_SORT_COLUMN, TAGS_DEFAULT_SORT_COLUMN_DIRECTION, TAGS_PAGE_DEFAULT_SIZE, TAGS_PAGE_SIZE_OPTIONS, TAGS_TABLE_HEADER } from 'src/app/conf/mtma-tags';
import { MtmaTag } from 'src/app/model/mtma-tag';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectMtmaActions, selectMtmaActiveTask, selectMtmaCommandAutomation, selectMtmaTagModify, selectMtmaTags, selectMtmaTagsMaxData, selectMtmaTagsPagination } from 'src/app/store/device/device.selector';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { Lang } from 'src/app/model/lang';
import { MtmaTask } from 'src/app/model/mtma-task';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { MtmaTagDialogComponent } from '../mtma-manage-tag-dialog/mtma-manage-tag-dialog.component';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { MtmaIndexTab } from 'src/app/model/device';

@Component({
	selector: 'app-mtma-manage-tag',
	templateUrl: './mtma-manage-tag.component.html',
	providers: [ConfirmationService, DialogService]
})

export class MtmaManageTagComponent implements OnInit, OnDestroy {
	// change activeIndex of device-tab-group for redirect into MTM Quick&Easy
	@Output() activeIndexChange = new EventEmitter<number>();

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	mtmaTags$: Observable<MtmaTag[] | undefined>;
	mtmaTagsSub: Subscription | undefined;
	mtmaTagsPagination$: Observable<ItemsPagination | undefined>;
	mtmaTagsPaginationSub: Subscription | undefined;
	mtmaTagsMaxData$: Observable<MtmaMaxData | undefined>;
	mtmaTagsMaxDataSub: Subscription | undefined;
	mtmaCommandAutomation$: Observable<Command[] | undefined>;
	mtmaCommandAutomationSub: Subscription | undefined;
	mtmaActions$: Observable<MtmaAction[] | undefined>;
	mtmaActionsSub: Subscription | undefined;
	mtmaTagModify$: Observable<boolean | undefined>;
	mtmaTagModifySub: Subscription | undefined;
	currentLang$: Observable<Lang> = new Observable();
	currentLangSub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	columns = TAGS_TABLE_HEADER;
	pagingSub: Subscription;
	pageSortSub: Subscription;
	paging: PaginationParams = {
		size: TAGS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: TAGS_DEFAULT_SORT_COLUMN,
		order: TAGS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = TAGS_PAGE_DEFAULT_SIZE;
	pageSizeOptions = TAGS_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	maxSizeMemorySub: Subscription;
	memorySize = 1;
	maxMemorySize = 1;

	tagLoading: boolean;
	mtmaTags: MtmaTag[];

	mtmaCommandAutomation?: Command[];

	currentSite: Site;

	mtmaActions: MtmaAction[] = [];
	mtmaActionsSuccessSub: Subscription;
	confirmDeleteSub: Subscription;

	isActiveTask = false;
	dateTransform: DatePipe;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;
	MIN_MD_WIDTH = 500;
	COLUMN_TITLE_MIN_WD = 647;
	MAX_MD_WIDTH = 1120;
	MIN_WIDTH_MX7 = 1700;
	LG_WIDTH = 1500;

	MAX_MD_WIDTH_SHORT = 858;

	constructor(
		//private dialog: MatDialog,
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.mtmaTags$ = this.store.select(selectMtmaTags);
		this.mtmaTagsPagination$ = this.store.select(selectMtmaTagsPagination);
		this.mtmaTagsMaxData$ = this.store.select(selectMtmaTagsMaxData);
		this.mtmaCommandAutomation$ = this.store.select(selectMtmaCommandAutomation);
		this.mtmaActions$ = this.store.select(selectMtmaActions);
		this.mtmaTagModify$ = this.store.select(selectMtmaTagModify);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
	}

	ngOnInit(): void {
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);

		this.dispatchGetTags();
		this.store.dispatch(DeviceActions.getMtmaActions());

		this.subscribeAll();
		this.onResize();
	}

	subscribeAll() {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetTags();
		}
		);
		this.mtmaTagsSub = this.mtmaTags$.subscribe(mtmaTags => {
			this.mtmaTags = mtmaTags;
			this.tagLoading = false;
		});
		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});
		this.pagingSub = this.mtmaTagsPagination$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			}
			);
		this.maxSizeMemorySub = this.mtmaTagsMaxData$.pipe(filter(p => !!p))
			.subscribe(mtmaTagsMaxData => {
				this.memorySize = mtmaTagsMaxData.Current;
				this.maxMemorySize = mtmaTagsMaxData.Max;
			}
			);
		this.mtmaCommandAutomationSub = this.mtmaCommandAutomation$.subscribe(mtmaCommandAutomation => {
			this.mtmaCommandAutomation = mtmaCommandAutomation;
			this.createTranslationAction();
		});
		this.mtmaActionsSub = this.mtmaActions$.subscribe(mtmaActions => {
			this.mtmaActions = mtmaActions;
			this.getAutomationCommand();
			this.createTranslationAction();
		});
		this.currentLangSub = this.currentLang$.subscribe(() => {
			this.store.dispatch(DeviceActions.getMtmaActions());
		});
		this.mtmaTagModifySub = this.mtmaTagModify$.pipe(filter(mtmaTagModify => !!mtmaTagModify))
			.subscribe(() => {
				this.dispatchGetTags();
			});
		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id !== '') {
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetTags();
			});
	}

	ngOnDestroy(): void {
		this.filterSub?.unsubscribe();
		this.mtmaTagsSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.pagingSub?.unsubscribe();
		this.maxSizeMemorySub?.unsubscribe();
		this.mtmaCommandAutomationSub?.unsubscribe();
		this.mtmaActionsSub?.unsubscribe();
		this.currentLangSub?.unsubscribe();
		this.mtmaTagModifySub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
	}

	loadTags(event: TableLazyLoadEvent) {
		this.tagLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.dispatchGetTags();
	}

	dispatchGetTags(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaTags({ filter: this.filter, sort: this.paging.sort, order: this.paging.order, size: this.paging.size, page: this.paging.page }));
		}
	}

	isSelectTab(): boolean {
		return this.tabIndex === MtmaIndexTab.MTM_TAG_RFID;
	}

	addTag(): void {
		this.store.dispatch(DeviceActions.resetTagForm());
		this.ref = this.dialogService.open(MtmaTagDialogComponent, {
			header: this.translate.instant('ADD_TAG'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {}
		});
	}

	editTag(tag: MtmaTag): void {
		this.store.dispatch(DeviceActions.resetTagForm());
		this.ref = this.dialogService.open(MtmaTagDialogComponent, {
			header: this.translate.instant('EDIT_TAG'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				tag
			},
		});
	}

	createTranslationAction(): void {
		if (this.mtmaActions && this.mtmaCommandAutomation) {
			for (const action of this.mtmaActions) {
				if (action.AutomationId) {
					action.Traslation = this.getTranslationAction(action);
				} else {
					action.Traslation = this.translate.instant(action.Description);
				}
			}
		}
	}

	getTranslationAction(action: MtmaAction): string {
		for (const command of this.mtmaCommandAutomation) {
			let nameDevice = '';
			if (this.currentSite && this.currentSite.Devices) {
				for (const device of this.currentSite.Devices) {
					if (device.Id === command.AutomationId) {
						nameDevice = device.Name + ' - ';
					}
				}
			}
			if (action.AutomationId === command.AutomationId && action.Command === command.Id) {
				return nameDevice + command.Description;
			}
		}
		return '';
	}

	async getAutomationCommand(): Promise<void> {
		const mapAutomation = new Map<number, number>();
		for (const action of this.mtmaActions) {
			if (action.AutomationId && !mapAutomation.has(action.AutomationId)) {
				mapAutomation.set(action.AutomationId, action.AutomationId);
			}
		}
		let cont = 0;
		for (const key of mapAutomation.keys()) {
			setTimeout(() => {
				this.store.dispatch(DeviceActions.getMtmaAutomationCommands({ deviceId: key }));
			}, cont * 1000);
			cont++;
		}
	}

	getTextAction(tag: MtmaTag): string {
		let text = '';
		if (tag && tag.Actions) {
			for (const action of tag.Actions) {
				for (const actionAll of this.mtmaActions) {
					if (action === actionAll.Id) {
						if (text === '') {
							text = actionAll.Traslation;
						} else {
							text = text + ', ' + actionAll.Traslation;
						}
						break;
					}
				}
			}
		}
		return text;
	}

	delete(tag: MtmaTag): void {
		/*const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			minWidth: '20vw',
			maxWidth: '50vw',
			data: { title: 'DELETE_TAG', body: 'DELETE_TAG_WARNING' }
		});
		this.confirmDeleteSub = dialogRef.afterClosed().subscribe(result => {
			if (result === 'success') {
				this.store.dispatch(DeviceActions.deleteMtmaTag({ tagId:tag.ObjId }));
			}
		});*/

		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_TAG'),
			message: this.translate.instant('DELETE_TAG_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteMtmaTag({ tagId: tag.ObjId }));
			},
			reject: () => {
			}
		});
	}

	getRuleInfo(tag: MtmaTag): string {
		let res = '';
		if (tag.RuleId !== undefined) {
			res = this.translate.instant('GROUP') + ': ' + tag.RuleName;
		}
		return res;
	}

	getTagDates(tag: MtmaTag): string {
		let res = '';
		if (tag.DtFrom !== undefined && tag.DtTo !== undefined) {
			res = res + ' ' + this.translate.instant('FROM') + ' ' + this.dateTransform.transform(tag.DtFrom, 'dd/MM/yyyy HH:mm') +
				' - ' + this.translate.instant('TO') + ' ' + this.dateTransform.transform(tag.DtTo, 'dd/MM/yyyy HH:mm');
		}
		return res;
	}

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}

	goToMtmaQuickEasyTab() {
		this.activeIndexChange.emit(MtmaIndexTab.MTM_QUICKEASY);
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}

